import { useMemo } from 'react';

/**
 * VideoEmbed — renders a playable video from a URL. Supports Loom, YouTube,
 * and Vimeo (as responsive iframes) and self-hosted files (native <video>).
 * Unknown URLs fall back to an iframe embed of the raw URL.
 *
 * Used by announcement banners/modals where a `videoURL` may be present.
 */

type ParsedVideo = { kind: 'file' | 'iframe'; src: string };

export function parseVideoUrl(url: string): ParsedVideo | null {
  const u = (url ?? '').trim();
  if (!u) return null;

  // Loom — share or embed links.
  const loom = u.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/);
  if (loom) return { kind: 'iframe', src: `https://www.loom.com/embed/${loom[1]}` };

  // YouTube — watch, youtu.be, shorts, or embed links.
  const yt = u.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  );
  if (yt) return { kind: 'iframe', src: `https://www.youtube.com/embed/${yt[1]}` };

  // Vimeo.
  const vimeo = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return { kind: 'iframe', src: `https://player.vimeo.com/video/${vimeo[1]}` };

  // Direct media file.
  if (/\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(u)) return { kind: 'file', src: u };

  // Fallback: try to embed the raw URL in an iframe.
  return { kind: 'iframe', src: u };
}

interface VideoEmbedProps {
  url: string;
  title?: string;
  className?: string;
}

export function VideoEmbed({ url, title, className }: VideoEmbedProps) {
  const parsed = useMemo(() => parseVideoUrl(url), [url]);
  if (!parsed) return null;

  if (parsed.kind === 'file') {
    return (
      <div className={`w-full overflow-hidden rounded-lg bg-black ${className ?? ''}`}>
        <video
          controls
          preload="metadata"
          src={parsed.src}
          className="w-full max-h-[70vh]"
        >
          <a href={parsed.src} target="_blank" rel="noopener noreferrer">
            Open video
          </a>
        </video>
      </div>
    );
  }

  return (
    <div
      className={`relative w-full overflow-hidden rounded-lg bg-black aspect-video ${className ?? ''}`}
    >
      <iframe
        src={parsed.src}
        title={title || 'Video'}
        className="absolute inset-0 h-full w-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}
